import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Delete, UtensilsCrossed } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useModule } from '../../context/ModuleContext';
import { pinAuthService, PinStaffMember } from '../../services/pinAuthService';
import { parseApiError } from '../../utils/errorUtils';
import { Permissions } from '../../constants/Permissions';
import './WaiterLoginPage.css';

type Step = 'company' | 'staff' | 'pin';

// The full permission set the seeded Waiter role is granted (DbInitializer.cs waiterCodes).
// A logged-in user whose permissions are a subset of this list — and who has Waiter.Access —
// is treated as "waiter-only" for redirect purposes, regardless of what their role is named.
// This intentionally decouples routing from role display names, which are user-editable.
const WAITER_ONLY_PERMISSIONS: string[] = [
  Permissions.Waiter.Access,
  Permissions.Orders.View,
  Permissions.Orders.Create,
  Permissions.Orders.IssueKot,
  Permissions.Orders.UpdateStatus,
  Permissions.Menu.View,
  Permissions.Modifiers.View,
  Permissions.Tables.View,
  Permissions.Reservations.View,
  Permissions.Customers.View,
];

function isWaiterOnlyUser(permissions: string[] | undefined): boolean {
  if (!permissions || permissions.length === 0) return false;
  if (!permissions.includes(Permissions.Waiter.Access)) return false;
  return permissions.every((p) => WAITER_ONLY_PERMISSIONS.includes(p));
}

// Same pattern for the Kitchen role — KDS-only staff skip the app switcher too.
const KITCHEN_ONLY_PERMISSIONS: string[] = [
  Permissions.Kitchen.Access,
  Permissions.Orders.View,
  Permissions.Orders.ViewAll,
  Permissions.Orders.UpdateStatus,
];

function isKitchenOnlyUser(permissions: string[] | undefined): boolean {
  if (!permissions || permissions.length === 0) return false;
  if (!permissions.includes(Permissions.Kitchen.Access)) return false;
  return permissions.every((p) => KITCHEN_ONLY_PERMISSIONS.includes(p));
}

const WaiterLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setCompany } = useModule();

  const [step, setStep] = useState<Step>('company');
  const [companyCode, setCompanyCode] = useState('');
  const [staff, setStaff] = useState<PinStaffMember[]>([]);
  const [selected, setSelected] = useState<PinStaffMember | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyCode.trim()) return;
    setError(null);
    setLoading(true);
    try {
      const res = await pinAuthService.getStaff(companyCode.trim());
      const list = (res?.data as any)?.data ?? res?.data ?? [];
      if (!Array.isArray(list) || list.length === 0) {
        setError('No staff with PIN login found for this restaurant code.');
        return;
      }
      setStaff(list);
      setStep('staff');
    } catch (err) {
      setError(parseApiError(err, 'Restaurant code not found.').message);
    } finally {
      setLoading(false);
    }
  };

  const pickStaff = (member: PinStaffMember) => {
    setSelected(member);
    setPin('');
    setError(null);
    setStep('pin');
  };

  const pressDigit = (digit: string) => {
    if (pin.length >= 6) return;
    setError(null);
    setPin((prev) => prev + digit);
  };

  const backspace = () => setPin((prev) => prev.slice(0, -1));

  const submitPin = async (fullPin: string) => {
    if (!selected || fullPin.length < 4) return;
    setError(null);
    setLoading(true);
    try {
      const res = await pinAuthService.login(companyCode.trim(), selected.id, fullPin);
      if (res.data.success && res.data.data) {
        const { token, user, company } = res.data.data;
        login(token, user);
        setCompany(company ?? null);
        const destination = isWaiterOnlyUser(user.permissions)
          ? '/waiter/my-orders'
          : isKitchenOnlyUser(user.permissions)
            ? '/kitchen'
            : '/apps';
        navigate(destination);
      }
    } catch (err) {
      setError(parseApiError(err, 'Invalid PIN. Please try again.').message);
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit once enough digits are entered (4-digit PINs submit immediately; longer PINs wait for Enter/6 digits)
  React.useEffect(() => {
    if (pin.length === 4 || pin.length === 6) {
      submitPin(pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="waiter-login-page">
      <div className="waiter-login-card">
        <div className="waiter-login-header">
          <UtensilsCrossed size={28} />
          <h1>Staff Login</h1>
        </div>

        {error && <div className="waiter-login-error">{error}</div>}

        {step === 'company' && (
          <form onSubmit={handleCompanySubmit} className="waiter-login-form">
            <label className="waiter-login-label">Restaurant Code</label>
            <input
              type="text"
              className="waiter-login-input"
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
              placeholder="e.g. GREENLEA"
              autoFocus
              maxLength={20}
              disabled={loading}
            />
            <button type="submit" className="waiter-login-btn" disabled={loading || !companyCode.trim()}>
              {loading ? 'Checking…' : 'Continue'}
            </button>
            <button type="button" className="waiter-login-link" onClick={() => navigate('/login')}>
              Use email &amp; password instead
            </button>
          </form>
        )}

        {step === 'staff' && (
          <>
            <button className="waiter-login-back" onClick={() => setStep('company')}>
              <ArrowLeft size={16} /> Back
            </button>
            <p className="waiter-login-subtitle">Who's ordering?</p>
            <div className="waiter-staff-grid">
              {staff.map((member) => (
                <button key={member.id} className="waiter-staff-tile" onClick={() => pickStaff(member)}>
                  <span className="waiter-staff-avatar">
                    {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                  </span>
                  <span className="waiter-staff-name">{member.firstName} {member.lastName}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'pin' && selected && (
          <>
            <button className="waiter-login-back" onClick={() => setStep('staff')}>
              <ArrowLeft size={16} /> Back
            </button>
            <p className="waiter-login-subtitle">Hi {selected.firstName}, enter your PIN</p>
            <div className="waiter-pin-dots">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className={`waiter-pin-dot${i < pin.length ? ' filled' : ''}`} />
              ))}
            </div>
            <div className="waiter-pin-pad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} className="waiter-pin-key" onClick={() => pressDigit(d)} disabled={loading}>
                  {d}
                </button>
              ))}
              <span />
              <button className="waiter-pin-key" onClick={() => pressDigit('0')} disabled={loading}>0</button>
              <button className="waiter-pin-key waiter-pin-key--action" onClick={backspace} disabled={loading}>
                <Delete size={20} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WaiterLoginPage;
